import { FeatureFlag, Site } from '@ps/web-core-enums';
import { fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { NotificationType } from '@swf/components/StarsMPNotification/enums';
import type { EffectParams, RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import type {
  BaseMPNotification,
  ChestRewardMPNotification,
  FixedPrizeDrawIssuedMPNotification,
  GameTicketMPNotification,
  GeoComplyPLC,
  Notification,
  NotificationMessage
} from '@swf/core/types/Notification';
import { logout } from '@swf/core/utils/actions';
import { handleNotifications } from '@swf/core/utils/notifications';

import { geoComplyPLC } from './geoComplyPLC/geoComplyPLC';

/**
 * For multipurpose notifications, get identifier, notification type and feature flag enabled
 * @param notification
 */
const getMPNotificationInfo = (notification: BaseMPNotification, rootState: RootState) => {
  const { type, state = 'issued' } = notification;
  let featureEnabledFlag = FeatureFlag.WebNotificationEnabled;
  let ticketId = '';

  if (type === NotificationType.MiniGameTicket) {
    ticketId = `gameTicket-${(notification as GameTicketMPNotification).attributes.ticketId}`;
    featureEnabledFlag = FeatureFlag.WebNotificationEnabled;
  } else if (type === NotificationType.FixedPrizeDrawBase) {
    ticketId = `fixedPrizeDrawTicket-${
      (notification as FixedPrizeDrawIssuedMPNotification).attributes.drawId
    }`;
    featureEnabledFlag = FeatureFlag.FixedPrizeDrawNotificationEnabled;
  } else if (type === NotificationType.ChestRewardBase) {
    ticketId = `chestRewardTicket-${
      (notification as ChestRewardMPNotification).attributes.chestId
    }`;
    featureEnabledFlag = FeatureFlag.WebMPCNotificationEnabled;
  }

  const isNotificationEnabled = select.feature.check(rootState, featureEnabledFlag);

  return { ticketId, state, isNotificationEnabled };
};

const effects = {
  async push(
    { headers: { type }, body }: NotificationMessage,
    { rootState, dispatch, request }: EffectParams
  ) {
    const isEmbedded = select.device.isEmbedded(rootState);
    const currentModal = select.modal.getCurrent(rootState)?.path as string;
    const isSiteUS = select.cms.isSiteUS(rootState);
    const site = select.cms.getSiteId(rootState);
    const isSiteCAON = site === Site.CAON;
    const license = select.account.getLicense(rootState);
    const locale = select.locale.get(rootState);
    const showVegasContent = select.cms.showVegasContent(rootState);
    const { remainingInactivitySeconds } = select.localSettings.get(rootState);
    const features = select.feature.get(rootState);

    const getClearedById = (ticketRollId: number) =>
      select.notification.getClearedById(rootState, ticketRollId);

    const getExpiringById = (ticketRollId: number) =>
      select.notification.getExpiringById(rootState, ticketRollId);

    const getIssuedById = (ticketRollId: number) =>
      select.notification.getIssuedById(rootState, ticketRollId);

    const getIsNotificationEnabled = async (notification: BaseMPNotification) =>
      await getMPNotificationInfo(notification, rootState).isNotificationEnabled;

    const handleGeoComply = (props: GeoComplyPLC) =>
      geoComplyPLC({
        ...props,
        isSiteUS,
        isSiteCAON,
        enqueue: dispatch.modal.enqueue,
        license,
        request: (props) => request({ ...select.cms.getRequestOptions(rootState), ...props })
      });

    const getRemainingTime = async (): Promise<number> => {
      const {
        body: { remainingSeconds: remainingTime }
      } = await request({ service: 'account.token.expiryTime' });

      return remainingTime;
    };

    await handleNotifications({
      accountUpdate: dispatch.account.update,
      addCleared: dispatch.notification.addCleared,
      addExpiring: dispatch.notification.addExpiring,
      addIssued: dispatch.notification.addIssued,
      body,
      currentModal,
      geoComplyPLC: handleGeoComply,
      getClearedById,
      getExpiringById,
      getIsNotificationEnabled,
      getIssuedById,
      getRemainingTime,
      isEmbedded,
      isSiteUS,
      license,
      locale,
      localSettingsSet: dispatch.localSettings.set,
      logout: dispatch.auth.logout,
      modal: dispatch.modal,
      remainingInactivitySeconds,
      showVegasContent,
      site,
      type,
      features
    });
  },
  async acceptBonus(
    payload: {
      acceptBonus: boolean;
      rollId: number | string;
      status: number;
      notificationId: number;
    },
    { dispatch, request }: EffectParams
  ) {
    let hasError = false;
    const params = {
      service: payload.acceptBonus ? 'notification.roll.accept' : 'notification.roll.decline',
      body: {
        rollid: payload.rollId,
        status: payload.status,
        notificationid: payload.notificationId
      }
    };

    dispatch.notification.addIssued({ rollId: payload.rollId, submitted: true } as Notification);

    try {
      const { body } = await request(params);
      if (!body.success) {
        hasError = true;
      }
    } catch (error) {
      hasError = true;
    }

    dispatch.notification.addIssued({
      rollId: payload.rollId,
      hasError,
      submitted: false
    } as Notification);
  },

  delete(
    {
      notificationId
    }: {
      notificationId?: number;
    },
    { request }: EffectParams
  ) {
    request({
      service: 'notification.delete',
      body: { notificationId }
    });
  },

  async acceptGameTicket(
    {
      gameTicketId,
      status,
      notificationId,
      rollId,
      acceptTicket
    }: {
      rollId: string;
      acceptTicket: boolean;
      gameTicketId: number;
      notificationId: number;
      status: number;
    },
    { request, dispatch }: EffectParams
  ) {
    let hasError = false;
    const params = {
      service: acceptTicket ? 'notification.gameticket.accept' : 'notification.gameticket.decline',
      body: { gameTicketId, status, notificationId }
    };

    dispatch.notification.addIssued({ rollId, submitted: true } as Notification);

    try {
      const { body } = await request(params);
      hasError = !body.success;

      dispatch.modal.close('/notifications');
    } catch (error) {
      hasError = true;
    }

    dispatch.notification.addIssued({ rollId, hasError, submitted: false } as Notification);
  },
  async setPendingNotifications(params: undefined, { rootState, dispatch }: EffectParams) {
    if (select.account.isCasinoRealityCheckRequired(rootState)) {
      dispatch.modal.unique({ path: '/notifications/casino-session-reminder' });
    }
    if (!select.account.isEmailValidated(rootState)) {
      dispatch.modal.unique({ path: '/notifications/confirm-email' });
    }
    if (select.account.getForcedCashoutEnabled(rootState)) {
      dispatch.modal.unique({ path: '/notifications/login-cash-out' });
    }
    if (
      select.account.getDepositConfirmLimits(rootState) ||
      select.account.getGameConfirmLimits(rootState)
    ) {
      dispatch.modal.unique({ path: '/notifications/login-limits' });
    }
  },
  getNotificationCounters(params: undefined, { dispatch, request, actions }: EffectParams) {
    const onLogout = logout(actions) as NonNullable<ReturnType<typeof logout>>;
    const requestNotificationCounters = () =>
      request({ service: 'notification.mystars.getcounters' })
        .then(({ body }) => dispatch.notification.addCounter(body))
        .catch((err) => undefined);
    requestNotificationCounters();
    fromEvent(document, 'starsresume')
      .pipe(takeUntil(onLogout))
      .subscribe(requestNotificationCounters);
  }
};

export default effects;
